import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, Link } from 'react-router-dom';
import { Home, BarChart3, Shield, Ticket, Heart, LayoutDashboard } from 'lucide-react';
import { useAdminAuth } from '../hooks/useAdminAuth';
import { useMasterAuth } from '../hooks/useMasterAuth';
import { useAuth } from '../contexts/AuthContext';

const BottomNavBar: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { canAccessAdminDashboard } = useAdminAuth();
  const { canAccessMasterDashboard } = useMasterAuth();
  const { isAuthenticated, user } = useAuth();

  const isRegularUser = isAuthenticated && user?.role === 'user';

  const navItems = [
    { 
      path: '/', 
      icon: Home, 
      label: t('navigation.home', 'Home'),
      active: location.pathname === '/' || location.pathname === '/search'
    },
    ...(isRegularUser ? [
      {
        path: '/tickets',
        icon: Ticket,
        label: t('navigation.tickets', 'Tickets'),
        active: location.pathname === '/tickets'
      },
      {
        path: '/favorites',
        icon: Heart,
        label: t('navigation.favorites', 'Favorites'),
        active: location.pathname === '/favorites'
      },
      {
        path: '/dashboard',
        icon: LayoutDashboard,
        label: t('navigation.dashboard', 'My Account'),
        active: location.pathname === '/dashboard'
      }
    ] : []),
    ...(canAccessAdminDashboard() ? [{
      path: '/admin-dashboard',
      icon: BarChart3,
      label: t('header.adminPanel', 'Admin'),
      active: location.pathname === '/admin-dashboard' || location.pathname === '/admin'
    }] : []),
    ...(canAccessMasterDashboard() ? [{
      path: '/master-dashboard',
      icon: Shield,
      label: t('header.masterDashboard', 'Master'),
      active: location.pathname === '/master-dashboard' || location.pathname === '/master'
    }] : [])
  ];

  return (
    <nav className="fixed bottom-0 left-0 right-0 glass-nav safe-area-pb z-50">
      <div className={`grid ${navItems.length === 4 ? 'grid-cols-4' : navItems.length === 3 ? 'grid-cols-3' : 'grid-cols-2'} py-2`}>
        {navItems.map((item) => {
          const IconComponent = item.icon;
          return (
            <Link
              key={item.path}
              to={item.path}
              className={`flex flex-col items-center justify-center py-3 px-1 mx-1 rounded-xl transition-all duration-200 ${
                item.active
                  ? 'text-white glass-strong shadow-lg scale-105 glow'
                  : 'text-white/70 hover:text-white glass-button'
              }`}
            >
              <IconComponent 
                size={24} 
                strokeWidth={item.active ? 2.5 : 2}
                className={item.active ? 'drop-shadow-sm' : ''}
              />
              <span className={`text-xs mt-1 font-medium ${item.active ? 'font-semibold' : ''}`}>
                {item.label}
              </span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
};

export default BottomNavBar;
